/**
 * Lobby component - create or join a game room
 */

import { useState, useEffect } from 'react';
import { socketActions } from '../../lib/socketActions';
import { useGameStore } from '../../stores/gameStore';
import { useSettingsStore } from '../../stores/settingsStore';
import { WaitingRoom } from './WaitingRoom';
import { TutorialModal } from '../ui/TutorialModal';

export function Lobby() {
  const status = useGameStore((s) => s.gameState?.status);

  // If we're in a room, show the waiting room
  if (status === 'waiting') {
    return <WaitingRoom />;
  }

  // Otherwise show the create/join screen
  return <LobbyHome />;
}

function LobbyHome() {
  const [mode, setMode] = useState<'none' | 'create' | 'join'>('none');
  const [playerName, setPlayerName] = useState('');
  const [roomCodeInput, setRoomCodeInput] = useState('');
  const [error, setError] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);

  const serverError = useGameStore((s) => (s.lastEvent?.type === 'ERROR' ? s.lastEvent.message : null));
  const isCreatingRoom = useGameStore((s) => s.isCreatingRoom);
  const isJoiningRoom = useGameStore((s) => s.isJoiningRoom);
  const hasSeenTutorial = useSettingsStore((s) => s.hasSeenTutorial);

  // Check for room code in URL (invite link)
  // Security: Only allow alphanumeric characters to prevent XSS
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const roomFromUrl = params.get('room');
    // Validate: exactly 4 alphanumeric characters only
    if (roomFromUrl && /^[A-Za-z0-9]{4}$/.test(roomFromUrl)) {
      setRoomCodeInput(roomFromUrl.toUpperCase());
      setMode('join');
    }
    // Always clean up URL to remove any query params
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Auto-show tutorial for first-time users
  useEffect(() => {
    if (!hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, [hasSeenTutorial]);

  const handleCreate = () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    setError('');
    socketActions.createRoom(playerName.trim());
  };

  const handleJoin = () => {
    if (!playerName.trim()) {
      setError('Please enter your name');
      return;
    }
    if (!roomCodeInput.trim() || roomCodeInput.length !== 4) {
      setError('Please enter a valid 4-letter room code');
      return;
    }
    setError('');
    socketActions.joinRoom(roomCodeInput.toUpperCase().trim(), playerName.trim());
  };

  return (
    <div className="min-h-content flex flex-col items-center justify-center p-4 safe-area-x">
      <div className="w-full max-w-md lg:max-w-lg">
        {/* Logo/Title */}
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">Welcome!</h2>
          <p className="text-slate-600 dark:text-slate-300">
            A fast-paced multiplayer word game where you claim and steal words.
          </p>
          <button
            onClick={() => setShowTutorial(true)}
            className="mt-2 px-3 py-2 text-blue-500 hover:text-blue-600 text-sm font-medium underline underline-offset-2"
          >
            How to Play
          </button>
        </div>

        {/* Name input - always visible */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
            Your Name
          </label>
          <input
            type="text"
            value={playerName}
            onChange={(e) => setPlayerName(e.target.value)}
            placeholder="Enter your name"
            maxLength={20}
            enterKeyHint="next"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder:text-slate-400 dark:placeholder:text-slate-500"
          />
        </div>

        {/* Mode selection */}
        {mode === 'none' && (
          <div className="space-y-3">
            <button
              onClick={() => setMode('create')}
              className="w-full py-4 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors"
            >
              Create New Game
            </button>
            <button
              onClick={() => setMode('join')}
              className="w-full py-4 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 font-semibold border border-slate-300 dark:border-slate-600 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              Join Existing Game
            </button>
          </div>
        )}

        {/* Create mode */}
        {mode === 'create' && (
          <div className="space-y-4">
            <button
              onClick={handleCreate}
              disabled={isCreatingRoom}
              className="w-full py-4 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isCreatingRoom && (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {isCreatingRoom ? 'Creating...' : 'Create Room'}
            </button>
            <button
              onClick={() => setMode('none')}
              disabled={isCreatingRoom}
              className="w-full py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Back
            </button>
          </div>
        )}

        {/* Join mode */}
        {mode === 'join' && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Room Code
              </label>
              <input
                type="text"
                value={roomCodeInput}
                onChange={(e) =>
                  setRoomCodeInput(e.target.value.toUpperCase().slice(0, 4))
                }
                placeholder="ABCD"
                maxLength={4}
                disabled={isJoiningRoom}
                autoFocus
                autoCapitalize="characters"
                autoCorrect="off"
                enterKeyHint="go"
                aria-label="Room code"
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-center text-2xl font-mono tracking-wider disabled:opacity-50 bg-white dark:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>
            <button
              onClick={handleJoin}
              disabled={isJoiningRoom}
              className="w-full py-4 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isJoiningRoom && (
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {isJoiningRoom ? 'Joining...' : 'Join Room'}
            </button>
            <button
              onClick={() => setMode('none')}
              disabled={isJoiningRoom}
              className="w-full py-2 text-slate-600 hover:text-slate-900 disabled:opacity-50"
            >
              Back
            </button>
          </div>
        )}

        {/* Error display - ARIA live region for screen readers */}
        {(error || serverError) && (
          <div
            role="alert"
            aria-live="assertive"
            className="mt-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400"
          >
            {error || serverError}
          </div>
        )}
      </div>

      {/* Tutorial modal */}
      <TutorialModal isOpen={showTutorial} onClose={() => setShowTutorial(false)} />
    </div>
  );
}
